import React from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart3, Command, Rocket, Brain, LineChart, Target, CheckCircle2 } from 'lucide-react';
import { useSound } from '../hooks/useSound';

const solutions = [
  {
    path: 'operational-excellence',
    icon: Command,
    title: 'Operational Excellence',
    description: 'Streamline operations and improve efficiency',
    features: [
      'Identifying inefficiencies in workflows and production processes',
      'Optimizing resource allocation for maximum productivity',
      'Enhancing quality control to maintain consistency',
      'Automating routine tasks to free up time'
    ]
  },
  {
    path: 'financial-performance',
    icon: LineChart,
    title: 'Financial Performance Optimization',
    description: 'Strengthen your financial position with AI-driven insights',
    features: [
      'Real-time cash flow and profitability analysis',
      'Cost-saving opportunities identification',
      'Revenue stream improvement recommendations',
      'Financial risks and market trends forecasting'
    ]
  },
  {
    path: 'market-strategy',
    icon: Target,
    title: 'Market Strategy & Business Intelligence',
    description: 'Make smarter decisions with daily AI-powered insights',
    features: [
      'Competitive benchmarking against industry leaders',
      'Market trends and demand forecasting',
      'Customer behavior analysis',
      'Industry-specific actionable insights'
    ]
  },
  {
    path: 'execution',
    icon: Rocket,
    title: 'Execution & Accountability',
    description: 'Turn insights into results with structured implementation',
    features: [
      'Structured business strategies with clear action steps',
      'AI-powered progress tracking',
      'Regular accountability check-ins',
      'Personalized guidance for implementation'
    ]
  }
];

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export const Solutions = () => {
  const navigate = useNavigate();
  const { playHover, playClick } = useSound();

  return (
    <div className="pt-24 px-4 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ...springConfig }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4">
            <span className="bg-gradient-to-r from-amber-200 to-amber-400 text-transparent bg-clip-text">
              Solutions to Optimize and Grow Your Business
            </span>
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto">
            At ProfitIQ.AI, we provide AI-powered business intelligence designed to help small and medium-sized businesses improve efficiency, maximize profitability, and stay ahead of the competition. Our solutions focus on practical, data-driven strategies tailored to your specific industry and business needs.
          </p>
        </motion.div>

        <Routes>
          <Route
            path="/"
            element={
              <div className="space-y-16">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {solutions.map((solution, index) => (
                    <motion.div
                      key={solution.path}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1, duration: 1, ...springConfig }}
                    >
                      <motion.div
                        whileHover={{ y: -5, scale: 1.02 }}
                        transition={{ duration: 0.8, ...springConfig }}
                        className="h-full p-6 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm group"
                        onMouseEnter={playHover}
                      >
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          transition={{ duration: 0.8, ...springConfig }}
                          className="w-12 h-12 mb-4 bg-amber-400/10 rounded-lg flex items-center justify-center group-hover:bg-amber-400/20 transition-colors duration-700"
                        >
                          <solution.icon className="w-6 h-6 text-amber-400" />
                        </motion.div>
                        <h3 className="text-xl font-semibold text-white mb-2">{solution.title}</h3>
                        <p className="text-gray-400 mb-4">{solution.description}</p>
                        <ul className="space-y-2">
                          {solution.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-gray-300">
                              <CheckCircle2 className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 1, ...springConfig }}
                  className="text-center p-8 bg-gradient-to-b from-amber-400/5 to-transparent rounded-xl border border-amber-400/10 backdrop-blur-sm"
                >
                  <h2 className="text-2xl font-bold text-white mb-4">Start Optimizing Your Business Today</h2>
                  <p className="text-gray-400 mb-6 max-w-2xl mx-auto">
                    With AI-driven insights and expert-backed strategies, ProfitIQ.AI empowers businesses to grow smarter and more efficiently.
                  </p>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={springConfig}
                    onMouseEnter={playHover}
                    onClick={() => {
                      playClick();
                      navigate('/consultation');
                    }}
                    className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold text-lg hover:shadow-lg hover:shadow-amber-400/20 transition-all duration-300"
                  >
                    Get Started
                  </motion.button>
                </motion.div>
              </div>
            }
          />
          {solutions.map((solution) => (
            <Route
              key={solution.path}
              path={solution.path}
              element={
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="max-w-4xl mx-auto"
                >
                  <h2 className="text-3xl font-bold mb-4 gradient-text">{solution.title}</h2>
                  <p className="text-gray-400">Detailed content for {solution.title}</p>
                </motion.div>
              }
            />
          ))}
        </Routes>
      </div>
    </div>
  );
};